/**
 * Button-template library and device buttons API (brief §4, ADR 0011).
 *
 * The fleet layer is a versioned per-vendor/model template; the device layer
 * is a device's own rows — `device` overrides a key, `phone_local` omits it
 * from rendered config so the phone's own programmed value persists.
 */

import { apiFetch } from "./client";

/** One key definition within a button template. */
export interface TemplateKey {
  key_index: number;
  key_type: string;
  target: string | null;
  label: string | null;
  line_role: "primary" | "backup" | "monitor" | null;
}

/** A button template with its keys. */
export interface ButtonTemplateRow {
  id: number;
  name: string;
  vendor: string;
  model: string;
  sidecar_count: number;
  description: string | null;
  version: number;
  /** The node an imported template's targets live on (ADR 0011, 2026-10-19); null = fleet template. */
  node_id: number | null;
  buttons: TemplateKey[];
}

/** A device-layer button row. */
export interface DeviceButtonRow {
  key_index: number;
  source: "device" | "phone_local";
  key_type: string | null;
  target: string | null;
  label: string | null;
  line_id: number | null;
}

/** One resolved (post-precedence) button the renderer would emit. */
export interface EffectiveButton {
  key_index: number;
  key_type: string | null;
  target: string | null;
  label: string | null;
  line_role: string | null;
  line_id: number | null;
  source: "template" | "device";
}

/** The per-device button view: layers plus the resolved layout. */
export interface DeviceButtonView {
  device: {
    id: number;
    mac: string;
    vendor: string;
    model: string;
    label: string | null;
    expansion_modules: number;
    provisioning_state: string;
    button_template_id: number | null;
  };
  template: ButtonTemplateRow | null;
  device_buttons: DeviceButtonRow[];
  effective: EffectiveButton[];
  /** The phone's identities in rendered order (identity 1 = the location's home node). */
  lines: DeviceButtonLine[];
}

/** One of the device's SIP identities, as a key can be bound to it. */
export interface DeviceButtonLine {
  id: number;
  identity: number;
  role: string;
  node_id: number;
  node_name: string;
  domain: string | null;
  extension: string;
}

export interface TemplateCreateIn {
  name: string;
  vendor: string;
  model: string;
  sidecar_count?: number;
  description?: string | null;
  buttons:
    Omit<TemplateKey, "target" | "label" | "line_role">[] | TemplateKey[];
}

export interface TemplateUpdateIn {
  description?: string | null;
  sidecar_count?: number | null;
  buttons: TemplateKey[];
}

export function getButtonTemplates(): Promise<{ items: ButtonTemplateRow[] }> {
  return apiFetch("/api/buttons/templates");
}

export function createButtonTemplate(
  body: TemplateCreateIn,
): Promise<ButtonTemplateRow> {
  return apiFetch("/api/buttons/templates", { method: "POST", body });
}

export function updateButtonTemplate(
  id: number,
  body: TemplateUpdateIn,
): Promise<ButtonTemplateRow> {
  return apiFetch(`/api/buttons/templates/${id}`, { method: "PUT", body });
}

export function deleteButtonTemplate(id: number): Promise<{ deleted: number }> {
  return apiFetch(`/api/buttons/templates/${id}`, { method: "DELETE" });
}

export function getDeviceButtons(deviceId: number): Promise<DeviceButtonView> {
  return apiFetch(`/api/buttons/devices/${deviceId}`);
}

export function setDeviceButtons(
  deviceId: number,
  buttons: DeviceButtonRow[],
): Promise<{ device_id: number; buttons: DeviceButtonRow[] }> {
  return apiFetch(`/api/buttons/devices/${deviceId}`, {
    method: "PUT",
    body: { buttons },
  });
}

/** Assign a template to a device, or clear the assignment with `null`. */
export function assignButtonTemplate(
  deviceId: number,
  templateId: number | null,
): Promise<{ device_id: number; button_template_id: number | null }> {
  return apiFetch(`/api/buttons/devices/${deviceId}/template`, {
    method: "POST",
    body: { template_id: templateId },
  });
}

/** One thing the Vodia import could not carry over (ADR 0054). */
export interface ButtonImportFinding {
  kind: string;
  node: string;
  domain: string;
  subject: string;
  key: number | null;
  detail: string;
}

export interface ButtonImportReport {
  dry_run: boolean;
  nodes_read: number;
  templates: {
    name: string;
    node: string;
    domain: string;
    vodia_id: string;
    vendor: string;
    model: string;
    sidecar_count: number;
    keys: number;
    skipped: number;
  }[];
  devices: {
    mac: string;
    device_id: number;
    node: string;
    domain: string;
    extension: string;
    template: string | null;
    device_keys: number;
    phone_local_keys: number;
  }[];
  findings: ButtonImportFinding[];
  summary: { templates: number; devices: number; findings: number };
  applied?: { templates_created: number; templates_updated: number; devices: number };
}

/** Plan (dry run) or apply the import of Vodia's button layouts (ADR 0054). */
export function importVodiaButtons(
  dryRun: boolean,
  selection?: { templates: string[]; devices: string[] },
): Promise<ButtonImportReport> {
  return apiFetch("/api/buttons/import/vodia", {
    method: "POST",
    body: { dry_run: dryRun, ...(selection ?? {}) },
  });
}
